import React, { useEffect, useRef } from 'react';
import { type State, useHookstate, Downgraded } from '@hookstate/core';
import {  Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import Map from '~/features/Map/components/Map';
import type { IGeographical } from '../../interfaces';
import DrawInMap from '../DrawInMap';
import 'leaflet-draw/dist/leaflet.draw.css';
import AreaList from '../AreaList';
import { LoadingButton } from '@mui/lab';
import { useLang } from '~/hooks/useLang';
import CloseIcon from '@mui/icons-material/Close';
import LocalizeAddressControl from '../LocalizeAddressControl';

interface ModalDrawMapProps {
  readonly isOpen: State<boolean>
  readonly geographicalArea: State<IGeographical[]>
}

function MapContent({ geographicalArea, featureGroupRef }: { geographicalArea: State<IGeographical[]>, featureGroupRef: React.RefObject<any> }) {
  return (
    <>
      <LocalizeAddressControl position="topright" />
      <DrawInMap
        value={geographicalArea.get() ?? []}
        onChange={(value) => geographicalArea.set(value)}
        valueDrawOpen={geographicalArea.get() ?? []}
        featureGroupRef={featureGroupRef}
      />
    </>
  );
}

export default function ModalDrawMap({ isOpen, geographicalArea }: ModalDrawMapProps) {
  const { translate } = useLang();
  const featureGroupRef = useRef<any>(null);
  const localGeographicalArea = useHookstate<IGeographical[]>([]);

  useEffect(() => {
    if (isOpen.get()) {
      const currentAreas = geographicalArea.attach(Downgraded).get();
      localGeographicalArea.set(JSON.parse(JSON.stringify(currentAreas)));
    }
  }, [isOpen.get()]);

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      handleClose();
    }
  };

  const handleClose = () => {
    localGeographicalArea.set([]);
    isOpen.set(false);
  }

  const handleSave = () => {
    const areas = localGeographicalArea.attach(Downgraded).get();
    const renumberedAreas = areas.map((area, idx) => ({
      ...area,
      name: `Area ${idx + 1}`
    }));
    
    geographicalArea.set(renumberedAreas);
    isOpen.set(false);
  }

  const handleDelete = (name: string) => {
    const currentAreas = localGeographicalArea.attach(Downgraded).get();
    const updatedAreas = currentAreas.filter((area) => area.name !== name);
    localGeographicalArea.set(updatedAreas);
    
    if (featureGroupRef.current) {
      featureGroupRef.current.eachLayer((layer: any) => {
        if (layer.options.className === name) {
          featureGroupRef.current.removeLayer(layer);
        }
      });
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth={'lg'}
      open={isOpen?.get()}
      disableEscapeKeyDown={true}
      onClose={clickOutModalClose}
    >
      <DialogTitle display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
        {translate('Select areas')}
        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Map
          height='500px'
          enableLayerControl={false}
        >
          <MapContent geographicalArea={localGeographicalArea} featureGroupRef={featureGroupRef} />
        </Map>
        <AreaList 
          areas={localGeographicalArea.attach(Downgraded).get() ?? []} 
          onDelete={handleDelete}
        />
      </DialogContent>
      <DialogActions sx={{ paddingRight: '24px', paddingBottom: '24px' }}>
        <Button
          variant='outlined'
          onClick={() => handleClose()}
        >
          {translate('Cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
        >
          {translate('Save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}